import React, { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Engine-computed rates over ten consecutive computation cycles. Deliberately
// volatile so the dampening effect is visible. Illustrative only.
const COMPUTED: number[] = [100, 138, 121, 92, 150, 71, 166, 109, 97, 142]

const W = 520
const H = 200
const PAD_X = 36
const PAD_Y = 24

export default function RateStabilityVisualizer() {
  const [incPct, setIncPct] = useState(15)
  const [decPct, setDecPct] = useState(5)

  const published = useMemo(() => {
    const out: number[] = [COMPUTED[0]]
    for (let i = 1; i < COMPUTED.length; i++) {
      const prev = out[i - 1]
      const maxUp = prev * (1 + incPct / 100)
      const maxDown = prev * (1 - decPct / 100)
      out.push(Math.round(Math.min(Math.max(COMPUTED[i], maxDown), maxUp)))
    }
    return out
  }, [incPct, decPct])

  const { min, max } = useMemo(() => {
    const all = [...COMPUTED, ...published]
    const lo = Math.min(...all)
    const hi = Math.max(...all)
    const pad = (hi - lo) * 0.15
    return { min: Math.floor(lo - pad), max: Math.ceil(hi + pad) }
  }, [published])

  const x = (i: number) =>
    PAD_X + (i / (COMPUTED.length - 1)) * (W - PAD_X * 2)
  const y = (v: number) =>
    H - PAD_Y - ((v - min) / (max - min)) * (H - PAD_Y * 2)

  const computedPath = COMPUTED.map((v, i) => `${x(i)},${y(v)}`).join(' ')
  const publishedPath = published.map((v, i) => `${x(i)},${y(v)}`).join(' ')

  const dampenedCount = published.filter(
    (v, i) => Math.round(v) !== Math.round(COMPUTED[i])
  ).length

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Max increase per cycle
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={1}
              max={50}
              value={incPct}
              onChange={(e) => setIncPct(parseInt(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-500"
            />
            <div className="relative w-20 shrink-0">
              <Input
                type="number"
                min={1}
                max={50}
                value={incPct}
                onChange={(e) => setIncPct(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                className="h-8 pr-6 text-sm tabular-nums"
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">
            Max decrease per cycle
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={1}
              max={50}
              value={decPct}
              onChange={(e) => setDecPct(parseInt(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-rose-500"
            />
            <div className="relative w-20 shrink-0">
              <Input
                type="number"
                min={1}
                max={50}
                value={decPct}
                onChange={(e) => setDecPct(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                className="h-8 pr-6 text-sm tabular-nums"
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-lg border border-border/60 bg-white p-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Engine-computed rate versus the published rate after stability dampening across ten cycles">
          {/* baseline grid */}
          {[0, 0.5, 1].map((t) => (
            <line
              key={t}
              x1={PAD_X}
              x2={W - PAD_X}
              y1={PAD_Y + t * (H - PAD_Y * 2)}
              y2={PAD_Y + t * (H - PAD_Y * 2)}
              stroke="#e2e8f0"
              strokeWidth={1}
              strokeDasharray={t === 1 ? '0' : '3 3'}
            />
          ))}

          {/* computed (raw, volatile) */}
          <polyline
            points={computedPath}
            fill="none"
            stroke="#94a3b8"
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
          {COMPUTED.map((v, i) => (
            <circle key={`c${i}`} cx={x(i)} cy={y(v)} r={2.5} fill="#94a3b8" />
          ))}

          {/* published (stabilised) */}
          <polyline
            points={publishedPath}
            fill="none"
            stroke="#0d9488"
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          {published.map((v, i) => {
            const dampened = Math.round(v) !== Math.round(COMPUTED[i])
            return (
              <g key={`p${i}`}>
                <circle
                  cx={x(i)}
                  cy={y(v)}
                  r={dampened ? 4 : 3}
                  fill={dampened ? '#0d9488' : '#ffffff'}
                  stroke="#0d9488"
                  strokeWidth={2}
                />
                <text
                  x={x(i)}
                  y={y(v) - 9}
                  textAnchor="middle"
                  className="fill-foreground"
                  fontSize={9}
                  fontWeight={600}
                >
                  £{Math.round(v)}
                </text>
              </g>
            )
          })}

          {/* x-axis cycle ticks */}
          {COMPUTED.map((_, i) => (
            <text
              key={`x${i}`}
              x={x(i)}
              y={H - 6}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={8}
            >
              {i === 0 ? 'now' : `+${i}`}
            </text>
          ))}
        </svg>

        {/* Legend */}
        <div className="mt-1 flex items-center justify-center gap-5 text-[11px]">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <svg width="18" height="6" aria-hidden="true">
              <line x1="0" y1="3" x2="18" y2="3" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 3" />
            </svg>
            Engine computed
          </span>
          <span className="flex items-center gap-1.5 font-medium text-foreground">
            <svg width="18" height="6" aria-hidden="true">
              <line x1="0" y1="3" x2="18" y2="3" stroke="#0d9488" strokeWidth="2.5" />
            </svg>
            Published (stabilised)
          </span>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        <span className="font-medium tabular-nums text-foreground">{dampenedCount}</span> of{' '}
        {COMPUTED.length} cycles were dampened. The published line never jumps more than{' '}
        <span className="font-medium tabular-nums text-emerald-600">+{incPct}%</span> or falls
        more than <span className="font-medium tabular-nums text-rose-600">−{decPct}%</span> from
        the previous published rate — it tracks demand without the whiplash.
      </p>
    </div>
  )
}
